import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldCheck, Warning, Fire, ArrowsClockwise, CaretDown } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

/**
 * Code Health Card — surfaces the static code audit result in the Admin panel.
 * Runs the audit on demand, shows drift score + delta since last run, lists
 * the top offenders. Companion to the hourly runtime health monitor.
 */
export const CodeHealthCard = () => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchLatest = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/code-audit/latest`, { withCredentials: true });
      setAudit(res.data);
    } catch {
      // silent — card just won't populate
    }
  };

  const runNow = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/code-audit/run`, {}, { withCredentials: true });
      setAudit(res.data);
      toast.success(`Audit complete — drift score ${res.data.drift_score}`);
    } catch {
      toast.error('Audit failed — check backend logs');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !audit) fetchLatest();
  };

  useEffect(() => {
    // Prefetch on mount so the collapsed header can show the score
    fetchLatest();
  }, []);

  const status = audit?.status;
  const score = audit?.drift_score;
  const diff = audit?.diff_from_previous || {};

  const statusTheme = {
    healthy: { icon: ShieldCheck, color: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'Healthy' },
    drift_detected: { icon: Warning, color: 'text-amber-500', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', label: 'Drift' },
    critical: { icon: Fire, color: 'text-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Critical' },
    audit_failed: { icon: Warning, color: 'text-slate-500', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300', label: 'Failed' },
    never_run: { icon: ShieldCheck, color: 'text-slate-400', badge: 'bg-slate-100 text-slate-500 dark:bg-slate-900/40 dark:text-slate-400', label: 'Never run' },
  };
  const theme = statusTheme[status] || statusTheme.never_run;
  const StatusIcon = theme.icon;

  const DeltaChip = () => {
    if (diff?.is_first_run || typeof diff?.score_delta !== 'number') return null;
    const delta = diff.score_delta;
    if (delta === 0) return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">No change</span>;
    const up = delta > 0;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${up ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
        {up ? '+' : ''}{delta} since last
      </span>
    );
  };

  return (
    <Card className="mt-6" data-testid="code-health-section">
      <CardHeader className="cursor-pointer" onClick={handleToggle}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <StatusIcon weight="bold" size={18} className={theme.color} />
              Code Health
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${theme.badge}`}>{theme.label}</span>
            </CardTitle>
            <CardDescription>
              Static-tree audit — dead code, duplicate helpers, drift score. Runs daily + on demand.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {typeof score === 'number' && (
              <span className="text-xs text-muted-foreground" data-testid="code-health-score">
                Score: <span className="font-semibold text-foreground">{score}</span>/100
              </span>
            )}
            <DeltaChip />
            {audit?.timestamp && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {new Date(audit.timestamp).toLocaleDateString()}
              </span>
            )}
            <CaretDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Button size="sm" onClick={runNow} disabled={loading} data-testid="run-code-audit-btn">
              <ArrowsClockwise weight="bold" size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Scanning...' : 'Run Audit Now'}
            </Button>
            {audit?.trigger && (
              <span className="text-xs text-muted-foreground italic">
                Last run: {audit.trigger === 'scheduled' ? 'scheduled (daily)' : 'manual'}
              </span>
            )}
          </div>

          {!audit || audit.status === 'never_run' ? (
            <div className="text-center py-6 text-muted-foreground">
              <p>Run your first audit to establish a code-health baseline.</p>
              <p className="text-xs mt-1">After that, daily auto-scans will track drift over time.</p>
            </div>
          ) : audit.status === 'audit_failed' ? (
            <div className="text-center py-4 text-red-600">
              <p className="font-medium">Audit failed to run.</p>
              <p className="text-xs text-muted-foreground mt-1">{audit.error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Metric grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricTile
                  label="Zombie files"
                  value={audit.zombie_files?.length || 0}
                  hint="Unimported route modules"
                  testid="metric-zombies"
                />
                <MetricTile
                  label="Duplicate helpers"
                  value={audit.duplicate_helpers?.length || 0}
                  sublabel={`${audit.duplicate_helpers?.filter(d => !d.acknowledged).length || 0} unacknowledged`}
                  testid="metric-dupes"
                />
                <MetricTile
                  label="Dead models"
                  value={audit.dead_pydantic_models?.length || 0}
                  hint="Pydantic classes no one references"
                  testid="metric-dead-models"
                />
                <MetricTile
                  label="Oversized files"
                  value={audit.oversized_files?.length || 0}
                  hint=">= 2000 lines"
                  testid="metric-oversized"
                />
              </div>

              {/* Ruff row */}
              {audit.ruff?.available && (
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 border-t pt-2">
                  <span><span className="font-mono">F401</span> unused imports: <span className="font-semibold text-foreground">{audit.ruff.F401}</span></span>
                  <span><span className="font-mono">F841</span> unused locals: <span className="font-semibold text-foreground">{audit.ruff.F841}</span></span>
                  <span><span className="font-mono">F821</span> undefined names: <span className={`font-semibold ${audit.ruff.F821 > 0 ? 'text-red-500' : 'text-foreground'}`}>{audit.ruff.F821}</span></span>
                </div>
              )}

              {/* Detail lists */}
              <DetailList
                title="Duplicate helpers"
                items={audit.duplicate_helpers}
                render={(d) => (
                  <>
                    <span className="font-mono text-xs font-semibold">{d.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">× {d.count}</span>
                    {d.acknowledged && <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">acknowledged</span>}
                    <div className="text-[11px] text-muted-foreground mt-0.5">{d.locations.join(' · ')}</div>
                  </>
                )}
              />

              <DetailList
                title="Dead Pydantic models"
                items={audit.dead_pydantic_models}
                render={(m) => (
                  <>
                    <span className="font-mono text-xs font-semibold">{m.name}</span>
                    <span className="text-[11px] text-muted-foreground ml-2">{m.location}</span>
                  </>
                )}
              />

              <DetailList
                title="Zombie files"
                items={audit.zombie_files}
                render={(z) => (
                  <>
                    <span className="font-mono text-xs font-semibold">{z.path}</span>
                    <span className="text-[11px] text-muted-foreground ml-2">{z.lines} lines</span>
                  </>
                )}
              />

              <DetailList
                title="Oversized files"
                items={audit.oversized_files}
                render={(f) => (
                  <>
                    <span className="font-mono text-xs font-semibold">{f.path}</span>
                    <span className="text-[11px] text-muted-foreground ml-2">{f.lines} lines</span>
                    {f.critical && <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">critical</span>}
                  </>
                )}
              />

              {/* Diff summary */}
              {!diff.is_first_run && (diff.new_duplicates?.length || diff.new_zombies?.length || diff.new_dead_models?.length) ? (
                <div className="border-t pt-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-2">New since last scan</div>
                  {diff.new_duplicates?.length > 0 && <div className="text-xs text-muted-foreground">Duplicates: {diff.new_duplicates.join(', ')}</div>}
                  {diff.new_zombies?.length > 0 && <div className="text-xs text-muted-foreground">Zombies: {diff.new_zombies.join(', ')}</div>}
                  {diff.new_dead_models?.length > 0 && <div className="text-xs text-muted-foreground">Dead models: {diff.new_dead_models.join(', ')}</div>}
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const MetricTile = ({ label, value, hint, sublabel, testid }) => (
  <div className="border rounded-lg p-3 bg-muted/20" data-testid={testid}>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-xs font-medium">{label}</div>
    {sublabel && <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">{sublabel}</div>}
    {hint && !sublabel && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

const DetailList = ({ title, items, render }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title} ({items.length})</div>
      <div className="border rounded-lg divide-y divide-border">
        {items.slice(0, 12).map((it, i) => (
          <div key={i} className="px-3 py-2 text-sm">{render(it)}</div>
        ))}
        {items.length > 12 && <div className="px-3 py-2 text-xs text-muted-foreground italic">… +{items.length - 12} more</div>}
      </div>
    </div>
  );
};
